"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

/**
 * La case « accès immédiat et renonciation au droit de rétractation », posée
 * juste avant le bouton de paiement, avec le rappel des CGV.
 *
 * Sans elle, un client peut se rétracter pendant 14 jours et se faire
 * rembourser, même après avoir suivi les cours. Voir lib/withdrawalConsent.js.
 *
 * @param {string} consentKey clé du texte de la case, selon ce qui est acheté
 */
export default function WithdrawalConsent({
    checked,
    onChange,
    showError = false,
    consentKey = "checkout_withdrawal_consent_subscription",
    className = "",
}) {
    const t = useTranslations();

    return (
        <div className={className}>
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-gray-700 dark:text-grayDark-200">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                />
                <span>{t(consentKey)}</span>
            </label>
            {showError && !checked && (
                <p
                    role="alert"
                    className="mb-0 mt-2 text-xs font-medium text-red-500 dark:text-red-400"
                >
                    {t("checkout_withdrawal_required")}
                </p>
            )}
            <p className="mb-0 mt-2 text-xs text-gray-500 dark:text-grayDark-400">
                {t.rich("checkout_terms_notice", {
                    link: (chunks) => (
                        <Link
                            href="/terms-of-sale"
                            target="_blank"
                            className="underline underline-offset-2"
                        >
                            {chunks}
                        </Link>
                    ),
                })}
            </p>
        </div>
    );
}

/**
 * Écrit la trace de l'accord chez Stripe avant la confirmation du paiement.
 * N'échoue jamais bruyamment : un paiement ne doit pas être bloqué parce que
 * la trace n'a pas pu s'écrire.
 */
export async function recordWithdrawalConsent({ clientSecret, subscriptionId }) {
    if (!clientSecret) return;
    try {
        await fetch("/api/sales/withdrawal-consent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ clientSecret, subscriptionId }),
        });
    } catch (error) {
        console.error("Trace de renonciation non enregistrée:", error);
    }
}
